import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { HeaderPrimary } from "../../components/HeaderText/HeaderText.style";
import { authService } from "../../services/Services";
import { validatePassword, validatePasswordMatch } from "../../utils/passwordValidation";
import "./ResetPassword.css";

type ResendStatus = "idle" | "sending" | "sent" | "error";

interface LocationState {
    username?: string;
}

export default function ResetPassword() {
    const location = useLocation();
    const state = location.state as LocationState;

    const [username, setUsername] = useState(state?.username || "");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [passwordMismatch, setPasswordMismatch] = useState(false);
    const [resendStatus, setResendStatus] = useState<ResendStatus>("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    const handleResendCode = async () => {
        if (!username || resendStatus === "sending") return;
        setResendStatus("sending");
        try {
            await authService.forgotPassword(username);
            setResendStatus("sent");
        } catch {
            setResendStatus("error");
        }
    };

    const isFormValid = useMemo(() => {
        const passwordValidation = validatePassword(newPassword);
        const passwordsMatch = validatePasswordMatch(newPassword, confirmPassword);
        return (
            username.length > 0 &&
            code.length > 0 &&
            passwordValidation.isValid &&
            passwordsMatch &&
            newPassword.length > 0 &&
            confirmPassword.length > 0
        );
    }, [username, code, newPassword, confirmPassword]);

    const handlePasswordChange = (value: string) => {
        setNewPassword(value);
        if (value.length > 0) {
            const validation = validatePassword(value);
            setValidationErrors(validation.errors);
        } else {
            setValidationErrors([]);
        }
    };

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value);
        if (value.length > 0) {
            setPasswordMismatch(!validatePasswordMatch(newPassword, value));
        } else {
            setPasswordMismatch(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            setValidationErrors(passwordValidation.errors);
            return;
        }

        if (!validatePasswordMatch(newPassword, confirmPassword)) {
            setPasswordMismatch(true);
            return;
        }

        setLoading(true);

        try {
            await authService.confirmForgotPassword(username, code, newPassword);
            setSuccess(true);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Container className="ResetPasswordContainer">
                <Box className="ResetPasswordForm">
                    <HeaderPrimary>Password Reset Successful</HeaderPrimary>
                    <Box sx={{ mt: 3, mb: 3 }}>
                        <Alert severity="success">Your password has been successfully reset.</Alert>
                    </Box>
                    <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: "#043c4a",
                                height: "50px",
                                minWidth: "150px",
                                color: "#ffffff",
                                ":hover": {
                                    bgcolor: "#043c4a",
                                },
                            }}
                            onClick={() => navigate("/login")}
                        >
                            Go to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (success) {
        return (
            <Container className="ResetPasswordContainer">
                <Box className="ResetPasswordForm" sx={{ textAlign: "center" }}>
                    <HeaderPrimary>Password Reset Successful</HeaderPrimary>
                    <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
                        Your password has been successfully reset.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/login")}
                        sx={{
                            bgcolor: "#043c4a",
                            height: "50px",
                            minWidth: "150px",
                            color: "#ffffff",
                            ":hover": {
                                bgcolor: "#043c4a",
                            },
                        }}
                    >
                        Go to Login
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container className="ResetPasswordContainer">
            <form onSubmit={handleSubmit} className="ResetPasswordForm">
                <HeaderPrimary>Reset Password</HeaderPrimary>
                <Typography variant="body2" sx={{ textAlign: "center", mt: 2, mb: 3 }}>
                    Enter the verification code sent to your email and your new password.
                </Typography>

                {!state?.username && (
                    <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                        <TextField
                            label="Username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            fullWidth
                            sx={{ maxWidth: 300 }}
                        />
                    </Box>
                )}

                <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                    <TextField
                        label="Verification Code"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                        autoComplete="one-time-code"
                    />
                </Box>

                <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                    <TextField
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                        error={validationErrors.length > 0}
                    />
                </Box>

                <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                    <TextField
                        label="Confirm Password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                        error={passwordMismatch}
                        helperText={passwordMismatch ? "Passwords do not match" : ""}
                    />
                </Box>

                {validationErrors.length > 0 && (
                    <Box sx={{ maxWidth: 300, mx: "auto", mb: 2 }}>
                        <Alert severity="error">
                            <List dense disablePadding>
                                {validationErrors.map((error, index) => (
                                    <ListItem key={index} disablePadding>
                                        <ListItemText primary={error} primaryTypographyProps={{ variant: "body2" }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    </Box>
                )}

                {errorMessage && (
                    <Box sx={{ maxWidth: 300, mx: "auto", mb: 2 }}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}

                <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: "#043c4a",
                            height: "50px",
                            minWidth: "150px",
                            color: "#ffffff",
                            ":hover": {
                                bgcolor: "#043c4a",
                            },
                        }}
                        disabled={!isFormValid || loading}
                        type="submit"
                    >
                        {loading ? "Resetting..." : "Reset Password"}
                    </Button>
                </Box>

                <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Typography variant="body2">
                        Didn't receive a code?{" "}
                        <Link
                            to="#"
                            onClick={(e) => {
                                e.preventDefault();
                                handleResendCode();
                            }}
                            style={{ pointerEvents: resendStatus === "sending" ? "none" : "auto" }}
                        >
                            {resendStatus === "sending" ? "Sending..." : "Request a new one"}
                        </Link>
                    </Typography>
                    {resendStatus === "sent" && (
                        <Alert severity="success" sx={{ mt: 1, maxWidth: 300, mx: "auto" }}>
                            A new code has been sent to your email.
                        </Alert>
                    )}
                    {resendStatus === "error" && (
                        <Alert severity="error" sx={{ mt: 1, maxWidth: 300, mx: "auto" }}>
                            Failed to resend code. Please try again.
                        </Alert>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        <Link to="/login">Back to Login</Link>
                    </Typography>
                </Box>
            </form>
        </Container>
    );
}
